import L from 'leaflet';
import type { ApSummary, EstimateResult, Floor, ReferencePoint } from '@wrp/shared';
import type { Point } from './format';

/**
 * Local floor-plan coordinates are metres with the origin at the image's
 * bottom-left: x goes right, y goes up. Leaflet's CRS.Simple treats lat as the
 * vertical axis and lng as the horizontal one, so lat = y and lng = x.
 */
export function toLatLng(p: { x: number; y: number }): L.LatLng {
  return L.latLng(p.y, p.x);
}

export function fromLatLng(latlng: L.LatLng): Point {
  return { x: latlng.lng, y: latlng.lat };
}

export function floorBounds(floor: Floor): L.LatLngBounds {
  return L.latLngBounds(L.latLng(0, 0), L.latLng(floor.height_m, floor.width_m));
}

export function clampToFloor(floor: Floor, p: Point): Point {
  return {
    x: Math.min(floor.width_m, Math.max(0, p.x)),
    y: Math.min(floor.height_m, Math.max(0, p.y)),
  };
}

export function roundPoint(p: Point, digits = 2): Point {
  const f = 10 ** digits;
  return { x: Math.round(p.x * f) / f, y: Math.round(p.y * f) / f };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const CHECK_SVG =
  '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

/** Reference points are "pending" until at least one fingerprint has been recorded at them. */
export function referencePointIcon(
  rp: ReferencePoint,
  fingerprintCount: number,
  selected: boolean,
  effects: { pulse?: boolean; justDone?: boolean } = {},
): L.DivIcon {
  const completed = fingerprintCount > 0;
  const classes = [
    'map-pin',
    'map-pin--rp',
    completed ? 'is-done' : 'is-pending',
    selected ? 'is-selected' : '',
    effects.pulse ? 'is-pulse' : '',
    effects.justDone ? 'is-just-done' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return L.divIcon({
    className: 'map-pin-wrapper',
    html: `<div class="${classes}">
      <span class="map-pin__ring"></span>
      <span class="map-pin__dot">${completed ? CHECK_SVG : ''}</span>
      <span class="map-pin__label">${escapeHtml(rp.label)}</span>
    </div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

export function apIcon(ap: ApSummary, selected: boolean): L.DivIcon {
  const name = ap.ssid ?? ap.bssid;
  return L.divIcon({
    className: 'map-pin-wrapper',
    html: `<div class="map-pin map-pin--ap${selected ? ' is-selected' : ''}">
      <span class="map-pin__dot"></span>
      <span class="map-pin__label">${escapeHtml(name)}</span>
    </div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

export function estimateIcon(label: string): L.DivIcon {
  return L.divIcon({
    className: 'map-pin-wrapper',
    html: `<div class="map-pin map-pin--estimate">
      <span class="map-pin__dot"></span>
      <span class="map-pin__label">${escapeHtml(label)}</span>
    </div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

export function sampleIcon(state: { calibrated: boolean; selected: boolean; pulse: boolean }): L.DivIcon {
  const classes = [
    'map-pin',
    'map-pin--sample',
    state.calibrated ? 'is-calibrated' : '',
    state.selected ? 'is-selected' : '',
    state.pulse ? 'is-pulse' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return L.divIcon({
    className: 'map-pin-wrapper',
    html: `<div class="${classes}"><span class="map-pin__ring"></span><span class="map-pin__dot"></span></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

export function groundTruthIcon(compact = false): L.DivIcon {
  return L.divIcon({
    className: 'map-pin-wrapper',
    html: `<div class="map-pin map-pin--truth${compact ? ' map-pin--compact' : ''}">
      <span class="map-pin__dot"></span>
      ${compact ? '' : '<span class="map-pin__label">ground truth</span>'}
    </div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/** Grid spacing that keeps the number of lines reasonable for any floor size. */
export function gridStep(floor: Floor): number {
  const span = Math.max(floor.width_m, floor.height_m);
  for (const step of [0.5, 1, 2, 5, 10, 20, 50]) {
    if (span / step <= 30) return step;
  }
  return Math.ceil(span / 30);
}

export function buildGrid(floor: Floor): L.LayerGroup {
  const group = L.layerGroup();
  const step = gridStep(floor);
  const style: L.PolylineOptions = {
    color: '#38507a',
    weight: 1,
    opacity: 0.45,
    interactive: false,
  };
  for (let x = 0; x <= floor.width_m + 1e-9; x += step) {
    L.polyline([L.latLng(0, x), L.latLng(floor.height_m, x)], style).addTo(group);
  }
  for (let y = 0; y <= floor.height_m + 1e-9; y += step) {
    L.polyline([L.latLng(y, 0), L.latLng(y, floor.width_m)], style).addTo(group);
  }
  L.rectangle(floorBounds(floor), {
    color: '#5d7fbf',
    weight: 1.5,
    fill: false,
    interactive: false,
  }).addTo(group);
  return group;
}

export function estimateTooltip(result: EstimateResult): string {
  const parts = [`${result.algorithm.toUpperCase()} k=${result.k}`];
  if (result.position) parts.push(`${result.position.x.toFixed(2)}, ${result.position.y.toFixed(2)} m`);
  if (typeof result.error_m === 'number') parts.push(`error ${result.error_m.toFixed(2)} m`);
  return parts.join(' · ');
}
